/**
 * The connecting session's network coordinates.
 *
 * DEFAULT (every real deployment — deferred row D23). The portal sends NOTHING:
 * the core derives the address from the HTTP connection itself. The walled-garden
 * redirect is a plain destination-NAT, which rewrites only the DESTINATION, so the
 * request still arrives FROM the subscriber's captive-pool address — and a bare
 * `:80` DNAT cannot append query parameters, so `?ip=`/`?vlan=` are simply not
 * there in the field. `POST /captive/context` and `POST /captive/register` both
 * fall back to that connection address (RealIP-aware `requestSourceIP()` in
 * internal/captive/captive.go); `/register` additionally binds STRICTLY to the
 * caller's own address, refusing a body value that names a different one with 403
 * `captive.source_ip_mismatch`. Omitting the address is therefore both the
 * working path and the safe one.
 *
 * OVERRIDE (an access server that really does carry the context on the redirect
 * URL — the Amendment 02 shape, e.g. nginx `?ip=$remote_addr`): open the portal with
 * `?ip=<captive-pool-address>&vlan=<vlan-id>` (`?source_ip=` is accepted as an
 * alias). The values are echoed in the request bodies, where BOTH endpoints treat
 * `source_ip` as a cross-check of the connection address only — a value naming a
 * DIFFERENT address is refused with 403 `captive.source_ip_mismatch` *(security fix,
 * 2026-07-26: `/context` used to honour any address, which disclosed a neighbour's
 * MAC and switch/port to an unauthenticated caller)* — and `vlan` is accepted and
 * IGNORED, because a lease is keyed (address, VLAN) and a caller-named VLAN was a key
 * into somebody else's lease. An override that names another machine's address is
 * therefore useless; nothing in the flow depends on either value.
 */
export interface Connection {
  /** URL override of the source address; '' = let the core use the connection's. */
  sourceIp: string;
  /** URL override of the VLAN; 0 = absent. The core IGNORES it (address-only lookup). */
  vlan: number;
}

/** Parse the optional connection overrides from a URL query string. */
export function readConnection(search: string): Connection {
  const p = new URLSearchParams(search);
  const ip = (p.get('ip') ?? p.get('source_ip') ?? '').trim();
  const vlan = Number.parseInt(p.get('vlan') ?? '', 10);
  return { sourceIp: ip, vlan: Number.isFinite(vlan) && vlan > 0 ? vlan : 0 };
}

/**
 * The `{source_ip?, vlan?}` fields shared by the /captive/context and
 * /captive/register request bodies. Each key is present ONLY when the URL supplied
 * it; with no query parameters this is `{}` and the core resolves the address (and
 * an address-only lease lookup) from the connection.
 */
export function connectionFields(c: Connection): { source_ip?: string; vlan?: number } {
  const fields: { source_ip?: string; vlan?: number } = {};
  if (c.sourceIp !== '') fields.source_ip = c.sourceIp;
  if (c.vlan > 0) fields.vlan = c.vlan;
  return fields;
}
